import { AdminList, AdminDataGrid, GridColumn } from './shared';
/**
 * User List Component
 * Displays paginated list of users with search and filters
 * Includes questionnaire wave trigger functionality
 */

import { useState } from 'react';
import {
  TextField,
  EmailField,
  DateField,
  NumberField,
  SearchInput,
  SelectInput,
  ShowButton,
  useNotify,
  useRefresh,
  useListContext,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { PLAN_TIER_CHOICES } from '../constants';
import { PlanTierField, ListActions } from './shared';
import { adminApi } from '../providers/dataProvider';
import { logger } from '../utils/logger';
import { EffectivePlanField, EffectiveSourceField, EffectiveAccessField, PartnerField } from './users/UserAccessFields';
import { useAdminText } from './shared/useAdminText';

/**
 * Filters for user list
 */
const userFilters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Search by name or email..." />,
  <SelectInput
    key="planTier"
    source="planTier"
    label="Personal plan"
    choices={PLAN_TIER_CHOICES}
  />,
  <SelectInput
    key="subscriptionSource"
    source="subscriptionSource"
    label="Personal subscription source"
    choices={[
      { id: 'free', name: 'Free' },
      { id: 'manual', name: 'Offered access' },
      { id: 'revenuecat', name: 'RevenueCat' },
    ]}
  />,
  <SelectInput
    key="hasProfile"
    source="hasProfile"
    choices={[
      { id: 'true', name: 'Has Profile' },
      { id: 'false', name: 'No Profile' },
    ]}
  />,
];

/**
 * Custom list actions with questionnaire wave trigger
 */
function UserListActions() {
  const notify = useNotify();
  const refresh = useRefresh();
  const [showDialog, setShowDialog] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const handleTriggerAll = async () => {
    setTriggering(true);
    try {
      const response = await adminApi.triggerQuestionnaireWave();
      notify(`Questionnaire wave notifications sent to ${response.sentCount} users`, { type: 'success' });
      setShowDialog(false);
      refresh();
    } catch (error) {
      logger.error('Failed to trigger questionnaire wave:', error);
      notify('Failed to trigger questionnaire wave', { type: 'error' });
    } finally {
      setTriggering(false);
    }
  };

  return (
    <>
      <ListActions>
        <Button
          startIcon={<CampaignIcon />}
          onClick={() => setShowDialog(true)}
          size="small"
          sx={{ marginLeft: 1 }}
        >
          Trigger Wave
        </Button>
      </ListActions>

      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'warning.main' }}>
          Trigger Questionnaire Wave
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will send questionnaire wave notifications to <strong>all eligible users</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Eligible users are those who:
          </Typography>
          <Box component="ul" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
            <li>Have been active in the last 7 days</li>
            <li>Have not completed a wave in the last 7 days</li>
            <li>Have questions available to answer</li>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            To trigger for specific users only, select users in the list below and use the bulk action button.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} disabled={triggering}>
            Cancel
          </Button>
          <Button
            onClick={handleTriggerAll}
            variant="contained"
            color="warning"
            disabled={triggering}
            startIcon={triggering ? <CircularProgress size={20} color="inherit" /> : <CampaignIcon />}
          >
            {triggering ? 'Sending...' : 'Trigger for All Users'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Bulk action button for triggering questionnaire wave on selected users
 */
function TriggerWaveBulkAction() {
  const notify = useNotify();
  const refresh = useRefresh();
  const { selectedIds, onUnselectItems } = useListContext();
  const [showDialog, setShowDialog] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const handleTriggerSelected = async () => {
    setTriggering(true);
    try {
      const response = await adminApi.triggerQuestionnaireWave(selectedIds as string[]);
      notify(`Questionnaire wave notifications sent to ${response.sentCount} users`, { type: 'success' });
      setShowDialog(false);
      onUnselectItems();
      refresh();
    } catch (error) {
      logger.error('Failed to trigger questionnaire wave:', error);
      notify('Failed to trigger questionnaire wave', { type: 'error' });
    } finally {
      setTriggering(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<CampaignIcon />}
        onClick={() => setShowDialog(true)}
        size="small"
      >
        Trigger Wave ({selectedIds.length})
      </Button>

      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Trigger Questionnaire Wave
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will send questionnaire wave notifications to <strong>{selectedIds.length} selected user{selectedIds.length !== 1 ? 's' : ''}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Users will receive a notification prompting them to answer questionnaire questions.
            The notification will be translated to each user&apos;s preferred language.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} disabled={triggering}>
            Cancel
          </Button>
          <Button
            onClick={handleTriggerSelected}
            variant="contained"
            color="primary"
            disabled={triggering}
            startIcon={triggering ? <CircularProgress size={20} color="inherit" /> : <CampaignIcon />}
          >
            {triggering ? 'Sending...' : `Trigger for ${selectedIds.length} User${selectedIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function UserList() {
  const t = useAdminText();
  return (
    <AdminList
      filters={userFilters}
      actions={<UserListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
    >
      <AdminDataGrid omit={['id', 'updatedAt', 'planTier']}
        rowClick="show"
        bulkActionButtons={<TriggerWaveBulkAction />}
      >
        <TextField source="id" label="ID" />
        <EmailField source="email" />
        <TextField source="name" label="Name" />
        <GridColumn label={t('effectivePlan', 'Effective plan')} sortable={false}><EffectivePlanField /></GridColumn>
        <GridColumn label={t('subscriptionSource', 'Subscription source')} sortable={false}><EffectiveSourceField /></GridColumn>
        <GridColumn label={t('accessPeriod', 'Access period')} sortable={false}><EffectiveAccessField /></GridColumn>
        <GridColumn label={t('partner', 'Partner')} sortable={false}><PartnerField /></GridColumn>
        <GridColumn label={t('personalPlan', 'Personal plan')} source="planTier"><PlanTierField /></GridColumn>
        <NumberField source="cardSwapCount" label="Cards Swiped" />
        <DateField source="createdAt" label="Joined" showTime />
        <DateField source="updatedAt" label="Last Updated" showTime />
        <GridColumn label="Actions" sortable={false}><ShowButton /></GridColumn>
      </AdminDataGrid>
    </AdminList>
  );
}
